import { AppBar, Tabs, Tab } from '@mui/material';
import { useQuery } from 'react-query';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useEffect, useState, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Page, PageTitleWithBack } from '../../../components';
import {
  getNeighborhoodGeneralInfo,
  getNeighborhoodHoaInfo,
  getNeighborhoodSalesAndContractInfo,
  getNeighborhoodHouseSpecs
} from '../../../fetch';
import {
  INeighborhoodDetailGeneralInfo,
  INeighborhoodDetailSalesAndContractsInfo,
  INeighborhoodDetailHoaInfo,
  INeighborhoodDetailHouseSpecs
} from '../../../models';
import { GeneralInformation } from './general-information';
import { SalesAndContracts } from './sales-and-contracts';
import { PlatMap } from './plat-map';
import { HouseSpecifications } from './house-specs';
import { HOAInformationTab } from './hoa-info';
import { NeighborhoodOptionPage } from './options';
import { NeighborhoodAmenitiesPage } from './amenities';
import { SupervisorPm } from './supervisor-pm';

export const enum neighborhoodPages {
  genInfo,
  sales,
  hoa,
  specs,
  amenities,
  options,
  superPM,
  platMaps
}
interface IAddEditNeighborhood {}

export const AddEditNeighborhood: FC<IAddEditNeighborhood> = () => {
  const classes = useStyles();
  const [isLoadingSelectedNeighborhood] = useState(false);
  const [neighborhoodName, setNeighborhoodName] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<neighborhoodPages>(neighborhoodPages.genInfo);
  const [selectedTab, setSelectedTab] = useState(window.location.pathname);
  const { neighborhoodId }: { neighborhoodId: string } = useParams();
  const history = useHistory();

  const {
    isLoading: isLoadingGeneralInfo,
    data: generalInfo,
    refetch: refetchGeneralInfo
  } = useQuery<INeighborhoodDetailGeneralInfo, Error>(
    ['neighborhood-general-info', neighborhoodId],
    () => getNeighborhoodGeneralInfo(neighborhoodId),
    {
      onSuccess: d => setNeighborhoodName(d.name),
      enabled: neighborhoodId !== 'add'
    }
  );

  const {
    isLoading: isLoadingSalesInfo,
    data: salesAndContractsInfo,
    refetch: refetchSalesAndContractsInfo
  } = useQuery<INeighborhoodDetailSalesAndContractsInfo, Error>(
    ['neighborhood-sales-info', neighborhoodId],
    () => getNeighborhoodSalesAndContractInfo(neighborhoodId),
    {
      onSuccess: d => setNeighborhoodName(d.name),
      enabled: neighborhoodId !== 'add'
    }
  );

  const {
    isLoading: isLoadingNeighborhoodHoaInfo,
    data: hoaInfo,
    refetch: refetchHoaInfo
  } = useQuery<INeighborhoodDetailHoaInfo, Error>(
    ['neighborhood-hoa-info', neighborhoodId],
    () => getNeighborhoodHoaInfo(neighborhoodId),
    {
      onSuccess: d => setNeighborhoodName(d.name),
      enabled: neighborhoodId !== 'add'
    }
  );

  const {
    isLoading: isLoadingHouseSpecs,
    data: houseSpecsData,
    refetch: refetchHouseSpecs
  } = useQuery<INeighborhoodDetailHouseSpecs, Error>(
    ['neighborhood-house-specs', neighborhoodId],
    () => getNeighborhoodHouseSpecs(neighborhoodId),
    {
      onSuccess: d => setNeighborhoodName(d.name),
      enabled: neighborhoodId !== 'add'
    }
  );

  useEffect(() => {
    if (window.location.href.includes('general-information')) {
      setSelectedPage(neighborhoodPages.genInfo);
    }
    if (window.location.href.includes('sales-and-contracts')) {
      setSelectedPage(neighborhoodPages.sales);
    }
    if (window.location.href.includes('hoa-information')) {
      setSelectedPage(neighborhoodPages.hoa);
    }
    if (window.location.href.includes('house-specifications')) {
      setSelectedPage(neighborhoodPages.specs);
    }
    if (window.location.href.includes('amenities')) {
      setSelectedPage(neighborhoodPages.amenities);
    }
    if (window.location.href.includes('options')) {
      setSelectedPage(neighborhoodPages.options);
    }
    if (window.location.href.includes('supervisor-pm')) {
      setSelectedPage(neighborhoodPages.superPM);
    }
    if (window.location.href.includes('plat-maps')) {
      setSelectedPage(neighborhoodPages.platMaps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href, neighborhoodId]);

  const getNeighborhoodId = () => {
    return neighborhoodId ? neighborhoodId : 'add';
  };
  const neighborhoodLinks = useMemo(
    () =>
      [
        {
          name: 'General Information',
          url: `/development/neighborhoods/${getNeighborhoodId()}/general-information`
        },
        {
          name: 'Sales And Contracts',
          url: `/development/neighborhoods/${getNeighborhoodId()}/sales-and-contracts`
        },
        {
          name: 'HOA Information',
          url: `/development/neighborhoods/${getNeighborhoodId()}/hoa-information`
        },
        {
          name: 'House Specifications',
          url: `/development/neighborhoods/${getNeighborhoodId()}/house-specifications`
        },
        {
          name: 'Amenities',
          url: `/development/neighborhoods/${getNeighborhoodId()}/amenities`
        },
        {
          name: 'Options',
          url: `/development/neighborhoods/${getNeighborhoodId()}/options`
        },
        {
          name: 'Supervisor/PM',
          url: `/development/neighborhoods/${getNeighborhoodId()}/supervisor-pm`
        },
        neighborhoodId !== 'add' && {
          name: 'Plat Maps',
          url: `/development/neighborhoods/${getNeighborhoodId()}/plat-maps`
        }
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [neighborhoodId]
  );

  const showLoader =
    isLoadingSalesInfo ||
    isLoadingGeneralInfo ||
    isLoadingNeighborhoodHoaInfo ||
    isLoadingHouseSpecs;
  const title = showLoader
    ? 'Loading...'
    : !neighborhoodName && neighborhoodId === 'add'
    ? 'Add Neighborhood'
    : neighborhoodName || 'Edit Neighborhood';

  return (
    <Page title={title}>
      <>
        <PageTitleWithBack
          title={title}
          backText="Back to neighborhoods"
          backUrl="/development/neighborhoods"
        />
        {neighborhoodId !== 'add' && (
          <AppBar position="static" color="default" component="div" className={classes.tabBar}>
            <Tabs
              value={selectedTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_, value) => {
                setSelectedTab(value);
                history.push(value);
              }}
              variant="scrollable"
            >
              {neighborhoodLinks.map(link => {
                return (
                  <Tab
                    value={link.url}
                    key={link.name}
                    id={`${link.name}-information`}
                    aria-controls={`${link.name}-information`}
                    label={link.name}
                  />
                );
              })}
            </Tabs>
          </AppBar>
        )}
        {selectedPage === neighborhoodPages.genInfo && (
          <GeneralInformation
            generalInfo={generalInfo}
            isLoadingGeneralInfo={isLoadingGeneralInfo}
            refetchGeneralInfo={refetchGeneralInfo}
          />
        )}
        {selectedPage === neighborhoodPages.sales && (
          <SalesAndContracts
            salesAndContractsInfo={salesAndContractsInfo}
            isLoadingSalesInfo={isLoadingSalesInfo}
            refetchSalesAndContractsInfo={refetchSalesAndContractsInfo}
          />
        )}
        {selectedPage === neighborhoodPages.hoa && (
          <HOAInformationTab
            hoaInfo={hoaInfo}
            isLoadingNeighborhoodHoaInfo={isLoadingNeighborhoodHoaInfo}
            refetchHoaInfo={refetchHoaInfo}
          />
        )}
        {selectedPage === neighborhoodPages.platMaps && neighborhoodId !== 'add' && <PlatMap />}
        {selectedPage === neighborhoodPages.specs && !isLoadingSelectedNeighborhood && (
          <HouseSpecifications
            houseSpecsData={houseSpecsData}
            isLoadingHouseSpecs={isLoadingHouseSpecs}
            refetchHouseSpecs={refetchHouseSpecs}
          />
        )}
        {selectedPage === neighborhoodPages.amenities && !isLoadingSelectedNeighborhood && (
          <NeighborhoodAmenitiesPage neighborhoodId={neighborhoodId} />
        )}
        {selectedPage === neighborhoodPages.options &&
          !isLoadingSelectedNeighborhood &&
          neighborhoodId !== 'add' && <NeighborhoodOptionPage neighborhoodId={neighborhoodId} />}
        {selectedPage === neighborhoodPages.superPM && !isLoadingSelectedNeighborhood && (
          <SupervisorPm />
        )}
      </>
    </Page>
  );
};

const useStyles = makeStyles(theme => ({
  tabBar: {
    marginTop: '-1rem',
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  }
}));
